import { useEffect, useMemo, useState } from 'react';
import { Link2 } from 'lucide-react';
import { Modal, Button, Select, DatePicker } from '@anyemp/ui-kit';

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const daysBetween = (a, b) => Math.round((b.getTime() - a.getTime()) / 86400000);

const toISODate = (date) => {
  if (!date) return '';
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 10);
};

const StartPlaybookModal = ({ playbooks, existingActive = [], onClose, onConfirm }) => {
  const activePlaybookIds = useMemo(
    () => new Set(existingActive.map((i) => i.playbook_id)),
    [existingActive]
  );

  const options = useMemo(() => playbooks.map((pb) => ({
    value: String(pb.id),
    label: pb.name + (activePlaybookIds.has(pb.id) ? ' (already active)' : ''),
    disabled: activePlaybookIds.has(pb.id),
  })), [playbooks, activePlaybookIds]);

  const [playbookId, setPlaybookId] = useState(options.find((o) => !o.disabled)?.value || null);
  const [startedAt, setStartedAt] = useState(new Date());
  const [endsAt, setEndsAt] = useState(null);
  const [saving, setSaving] = useState(false);

  const playbook = playbooks.find((p) => String(p.id) === playbookId);
  const steps = playbook?.steps || [];
  const maxOffset = steps.reduce((m, s) => Math.max(m, s.offset_days || 0), 0);

  // When playbook changes, recompute default end date
  useEffect(() => {
    if (playbook && startedAt) {
      setEndsAt(addDays(startedAt, playbook.default_duration_days || 90));
    }
  }, [playbookId, startedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const periodDays = startedAt && endsAt ? daysBetween(startedAt, endsAt) : 0;
  const periodTooShort = periodDays < maxOffset;

  const canSubmit = playbookId && startedAt && endsAt && !periodTooShort && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await onConfirm({
        playbook_id: playbook?.id ?? playbookId,
        started_at: toISODate(startedAt),
        ends_at: toISODate(endsAt),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} size="medium" showCloseButton>
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-fg-primary pr-7">Start Playbook</h3>

        <div>
          <Select
            label="Playbook"
            value={playbookId}
            onChange={(value) => setPlaybookId(value)}
            options={options}
            placeholder="Select playbook"
            fullWidth
          />
          {playbook?.description && (
            <p className="mt-1 text-xs text-fg-tertiary">{playbook.description}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <DatePicker
            label="Start date"
            mode="single"
            value={startedAt}
            onChange={setStartedAt}
          />
          <DatePicker
            label="End date"
            mode="single"
            value={endsAt}
            onChange={setEndsAt}
            minDate={startedAt || undefined}
          />
        </div>

        {periodTooShort && (
          <p className="text-xs text-red-600 dark:text-red-400">
            Period ({periodDays} days) is too short. The last step needs at least {maxOffset} days from start.
          </p>
        )}

        {steps.length > 0 && (
          <div>
            <div className="text-xs font-medium text-fg-secondary mb-2">Preview ({steps.length} steps)</div>
            <div className="border border-divider rounded-md max-h-64 overflow-y-auto">
              {steps.map((step) => (
                <div key={step.id} className="px-3 py-2 border-b last:border-0 border-divider text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-fg-primary">{step.title}</span>
                    <span className="text-fg-tertiary whitespace-nowrap">
                      {startedAt ? addDays(startedAt, step.offset_days || 0).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) : `+${step.offset_days}d`}
                    </span>
                  </div>
                  {(step.links || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(step.links || []).map((link, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-300">
                          <Link2 className="h-3 w-3" />{link.title || 'link'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:flex-wrap sm:justify-end">
          <Button variant="outlined" color="neutral" onClick={onClose}>Cancel</Button>
          <Button color="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {saving ? 'Starting...' : 'Start'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default StartPlaybookModal;
